import React, { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import { IEditorProps } from '../Editor';
import { EditorLabel } from '../EditorLabel';
import { useEditorId } from '../hooks/useEditorId';
import { ChartInfo } from '@decisions/server/DecisionsFramework/Design/Report/ChartInfo';
import {
  SubCalcFieldDefinition,
  newSubCalcFieldDefinition,
} from '@decisions/server/DecisionsFramework/Design/Report/SubcalcFields/SubCalcFieldDefinition';
import { DesignerColor } from '@decisions/server/Decisions/Silverlight/UI/Utilities';
import { PropertyEditorContext } from 'components/property-editors/contexts/PropertyEditorContext';
import { ChartSeria } from './ChartSeria';
import './ChartSeriesEditor.less';
import { ChartType } from '@decisions/server/DecisionsFramework/Design/Report/ChartType';
import { EditorConfigButton } from 'components/Editors/EditorConfigButton';

interface IChartSeriesEditorProps extends IEditorProps {}
/**
 * A Chart Series Picker editor, which launches a picker dialog for groups.
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function ChartSeriesEditor({ child, path, onChange }: IChartSeriesEditorProps) {
  const [chartInfo, setChartInfo] = useState<ChartInfo>(child.EditorConfigurationValues[0].OutputValue as ChartInfo);
  const [id] = useEditorId(path);
  const context = useContext(PropertyEditorContext);
  const seriesCount = chartInfo?.FieldsDefinition?.length || 0;
  const [addSeriesDisablity, setAddSeriesDisablity] = useState(false);
  const selectableFields: SubCalcFieldDefinition[] = useMemo(() => {
    const data = chartInfo.FieldsDefinition;
    const result: SubCalcFieldDefinition[] = [];
    chartInfo?.AvailableSeries?.forEach(availableField => {
      const isUsed = !!data.filter(
        item =>
          item.FieldName === availableField.FieldName &&
          item.DataSource === availableField.DataSource &&
          item.TypeOfCalc === availableField.TypeOfCalc &&
          item.OutputName === availableField.OutputName
      ).length;

      if (!isUsed) {
        result.push(availableField);
      }
    });

    return result;
  }, [chartInfo]);

  const hideColorOption =
    seriesCount < 2 ||
    chartInfo.ChartType === ChartType.Bullet ||
    chartInfo.ChartType === ChartType.Counter ||
    chartInfo.ChartType === ChartType.Gauges;

  const comboSelectClick = useCallback(
    (newValue: SubCalcFieldDefinition, seriaNumber: number) => {
      const color = chartInfo.FieldsDefinition[seriaNumber].Color;
      const newChartInfo = chartInfo;
      newChartInfo.FieldsDefinition[seriaNumber] = { ...newValue, Color: color };
      setChartInfo(newChartInfo);

      child.Value.OutputValue[seriaNumber] = {
        ...child.Value.OutputValue[seriaNumber],
        ...newValue,
        Color: color,
      };

      onChange(child.Value.OutputValue, path);
    },
    [chartInfo, child.Value.OutputValue, onChange, path]
  );

  const setSeriesColor = useCallback(
    (designerColor: DesignerColor, seriaNumber: number) => {
      const newChartInfo = chartInfo;
      newChartInfo.FieldsDefinition[seriaNumber].Color = designerColor;
      setChartInfo(newChartInfo);

      child.Value.OutputValue[seriaNumber].Color = designerColor;
      onChange(child.Value.OutputValue, path);
    },
    [chartInfo, child.Value.OutputValue, onChange, path]
  );

  const addField = useCallback(async () => {
    if (selectableFields.length) {
      setAddSeriesDisablity(true);
      const newChartInfo = chartInfo;
      newChartInfo.FieldsDefinition.push(selectableFields[0]);
      setChartInfo(newChartInfo);

      child.Value.OutputValue.push({
        ...newSubCalcFieldDefinition(),
        ...selectableFields[0],
      });
      try {
        await onChange(child.Value.OutputValue, path);
      } finally {
        setAddSeriesDisablity(false);
      }
    }
  }, [chartInfo, child.Value.OutputValue, onChange, path, selectableFields]);

  const removeSeria = useCallback(
    (number: number) => {
      if (chartInfo.FieldsDefinition.length > 1) {
        const newChartInfo = chartInfo;
        newChartInfo.FieldsDefinition.splice(number, 1);
        setChartInfo(newChartInfo);

        child.Value.OutputValue.splice(number, 1);
        onChange(child.Value.OutputValue, path);
      }
    },
    [chartInfo, child.Value.OutputValue, onChange, path]
  );

  const swapFields = useCallback(
    (first: number, second: number) => {
      const newChartInfo = chartInfo;
      let buffer = newChartInfo.FieldsDefinition[second];
      newChartInfo.FieldsDefinition[second] = newChartInfo.FieldsDefinition[first];
      newChartInfo.FieldsDefinition[first] = buffer;
      setChartInfo(newChartInfo);

      buffer = child.Value.OutputValue[second];
      child.Value.OutputValue[second] = child.Value.OutputValue[first];
      child.Value.OutputValue[first] = buffer;

      onChange(child.Value.OutputValue, path);
    },
    [chartInfo, child.Value.OutputValue, onChange, path]
  );

  const moveUp = useCallback(
    (number: number) => {
      if (number > 0) {
        swapFields(number, number - 1);
      }
    },
    [swapFields]
  );

  const moveDown = useCallback(
    (number: number) => {
      if (number < chartInfo.FieldsDefinition.length - 1) {
        swapFields(number, number + 1);
      }
    },
    [chartInfo.FieldsDefinition.length, swapFields]
  );

  const clearColor = useCallback(
    (seriaNumber: number) => {
      setSeriesColor(null as any, seriaNumber);
    },
    [setSeriesColor]
  );

  const openColorPickerDialog = useCallback(
    (e: React.MouseEvent<Element>, seriaNumber: number) => {
      context.onShowDialogue({
        node: child,
        editingContextId: context.editingContextId,
        path,
        options: {
          currentValue: child.Value.OutputValue[seriaNumber].Color,
          onOk: (value: DesignerColor) => {
            setSeriesColor(value, seriaNumber);
          },
          onClear: () => {
            // typings don't get possible to set up 0
            clearColor(seriaNumber);
          },
          sourceElement: e.currentTarget,
        },
      });
    },
    [child, clearColor, context, path, setSeriesColor]
  );

  useEffect(() => {
    setChartInfo(child.EditorConfigurationValues[0].OutputValue as ChartInfo);
  }, [child.EditorConfigurationValues]);

  return (
    <EditorLabel hideInputLabel={true} htmlFor={id} editor={child} path={path} doNotWrap>
      <ul className="dpc-chart-series-editor">
        {chartInfo?.FieldsDefinition?.map((seria, i) => (
          <ChartSeria
            comboSelect={comboSelectClick}
            openColorPickerDialog={openColorPickerDialog}
            removeSeria={removeSeria}
            moveSeriaDown={moveDown}
            moveSeriaUp={moveUp}
            seriesLength={chartInfo.FieldsDefinition.length}
            clearColor={clearColor}
            hideColorOption={hideColorOption}
            seria={seria}
            selectableFields={selectableFields}
            seriaNumber={i}
            key={i}
          />
        ))}
      </ul>
      {chartInfo.IsMultiSeriesType && !!selectableFields.length && (
        <EditorConfigButton onClick={addField} disabled={addSeriesDisablity}>
          Add Series
        </EditorConfigButton>
      )}
    </EditorLabel>
  );
}
